import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { useRum } from '../../../containers/RumContext';
import { CALL_METHOD_TYPE } from '../../../store/realUserMonitoring';
import {
  apiZedocReviewQueueItems,
  apiZedocResolveReviewQueueItem,
  apiZedocDeleteReviewQueueItem,
} from '../../../common/api/zedoc';

/**
 * Custom hook to fetch and manage review queue items
 *
 * @param {Object} params - { patientName: string, clinicianId: number, domainId: string, status: string }
 * @returns {Object} { reviewQueueItems: Array, loading: boolean, error: Error | null, refetch: Function, resolveItem: Function, deleteItem: Function }
 */
export const useReviewQueueItems = ({
  page = 1,
  pageSize = 10,
  patientName,
  clinicianId,
  domainId,
  status,
} = {}) => {
  const dispatch = useDispatch();
  const awsRum = useRum();
  const [reviewQueueItems, setReviewQueueItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    setReviewQueueItems([]);
    setTotal(0);

    const apiParams = { page, pageSize };
    if (patientName) {
      apiParams.patientName = patientName;
    }
    if (clinicianId) {
      apiParams.clinicianId = clinicianId;
    }
    if (domainId) {
      apiParams.domainId = domainId;
    }
    if (status) {
      apiParams.status = status;
    }

    const rumStart = Date.now();
    dispatch(callMethod(apiZedocReviewQueueItems, apiParams))
      .then((result) => {
        awsRum?.recordEvent(CALL_METHOD_TYPE, {
          duration: Date.now() - rumStart,
          name: 'api.zedoc.reviewQueueItems',
          domainId,
          filtered: !!(patientName || clinicianId || domainId || status),
          pageIndex: page - 1,
        });
        setLoading(false);
        if (result && typeof result === 'object' && 'data' in result) {
          setReviewQueueItems(result.data || []);
          setTotal(result.total || 0);
        } else {
          setReviewQueueItems(result || []);
          setTotal((result || []).length);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error fetching review queue items:', err);
        setError(err);
      });
  }, [
    awsRum,
    dispatch,
    page,
    pageSize,
    patientName,
    clinicianId,
    domainId,
    status,
  ]);

  const resolveItem = useCallback(
    async (ingestErrorId, updates = {}) => {
      try {
        await dispatch(
          callMethod(apiZedocResolveReviewQueueItem, {
            ingestErrorId,
            updates,
          }),
        );
        // Refetch to update the list
        refetch();
        return { success: true };
      } catch (err) {
        console.error('Error resolving review queue item:', err);
        throw err;
      }
    },
    [dispatch, refetch],
  );

  const deleteItem = useCallback(
    async (ingestErrorId) => {
      try {
        await dispatch(
          callMethod(apiZedocDeleteReviewQueueItem, { ingestErrorId }),
        );
        // Refetch to update the list
        refetch();
        return { success: true };
      } catch (err) {
        console.error('Error deleting review queue item:', err);
        throw err;
      }
    },
    [dispatch, refetch],
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    reviewQueueItems,
    total,
    loading,
    error,
    refetch,
    resolveItem,
    deleteItem,
  };
};

export default useReviewQueueItems;
